import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import FormErrorMessage from 'components/FormErrorMessage/FormErrorMessage';
import { type FC, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useDiscoverySettingsContext } from '../../../composition/DiscoverySettings/discoverySettingsContext';
import { CloudResourceIdRuleEditorContent } from './CloudResourceIdRuleEditorContent';
import { CloudResourceTagRuleEditorContent } from './CloudResourceTagRuleEditorContent';
import { HostnameRuleEditorContent } from './HostnameRuleEditorContent';
import { RoutingSwitchingRuleEditorContent } from './RoutingSwitchingRuleEditorContent';
import type {
  RuleEditorContentProps,
  RuleEditorValues,
  SiteSeparationRuleType,
} from './RuleEditor.types';
import {
  type RuleEditorMode,
  getInitialMode,
  getInitialTestOpenSource,
  getRuleTypeOptions,
  getRuleValuesWithAppliedTestValues,
  getSidePanelTitle,
} from './RuleEditorSidePanel.helpers';
import { EditFooter, TestFooter } from './RuleEditorSidePanelFooter';
import { SlugRuleEditorContent } from './SlugRuleEditorContent';
import { SnmpLocationRuleEditorContent } from './SnmpLocationRuleEditorContent';
import { TestRuleSidePanelContent } from './TestRuleSidePanelContent';
import type { TestRuleValues } from './TestRuleSidePanelContent.helpers';
import { UsedAttributeDescription } from './UsedAttributeDescription';
import {
  GENERIC_VALIDATION_ERROR,
  SHARED_RULE_ERROR_FIELD,
  getRuleEditorValidationErrors,
  ruleEditorResolver,
} from './ruleEditorValidation';
import {
  createRuleFromEditorValues,
  getEditorDefaultValues,
  getTestRuleValuesFromEditor,
  isTestRuleAvailable,
} from './ruleEditorValues';

type RuleEditorSidePanelProps = {
  rule?: SiteSeparationRule;
  hasRoutingSwitchingRule: boolean;
  goToAuthenticationVendors?: () => void;
  initialMode?: 'edit' | 'test';
  onClose: () => void;
  onSubmit: (rule: SiteSeparationRule) => void;
};

const EDITOR_CONTENT_BY_TYPE: Record<
  SiteSeparationRuleType,
  FC<RuleEditorContentProps>
> = {
  regexHostname: HostnameRuleEditorContent,
  regexSnmpLocation: SnmpLocationRuleEditorContent,
  regexCloudResourceId: CloudResourceIdRuleEditorContent,
  cloudResourceTag: CloudResourceTagRuleEditorContent,
  routingSwitching: RoutingSwitchingRuleEditorContent,
  slug: SlugRuleEditorContent,
};

export const RuleEditorSidePanel = ({
  rule,
  hasRoutingSwitchingRule,
  goToAuthenticationVendors,
  initialMode,
  onClose,
  onSubmit,
}: RuleEditorSidePanelProps) => {
  const discoverySettingsContext = useDiscoverySettingsContext();
  const form = useCustomForm<RuleEditorValues>({
    defaultValues: getEditorDefaultValues(rule),
    resolver: ruleEditorResolver,
  });
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null,
  );
  const [mode, setMode] = useState<RuleEditorMode>(getInitialMode(initialMode));
  const [testValues, setTestValues] = useState<TestRuleValues | null>(null);
  const testOpenedFromTable = getInitialTestOpenSource(initialMode) === 'table';

  useEffect(() => {
    const defaultValues = getEditorDefaultValues(rule);
    form.reset(defaultValues);
    setSubmitErrorMessage(null);
    setMode(getInitialMode(initialMode));
    setTestValues(getTestRuleValuesFromEditor(defaultValues));
  }, [form, initialMode, rule]);

  const selectedType = useWatch({
    control: form.control,
    name: 'type',
  });

  const addMode = rule === undefined;
  const existingRuleId = rule?.id;
  const ruleTypeOptions = getRuleTypeOptions(
    selectedType,
    hasRoutingSwitchingRule,
  );
  const testRuleAvailable = isTestRuleAvailable(selectedType);
  const SelectedEditorContent = EDITOR_CONTENT_BY_TYPE[selectedType];

  const validateRuleValues = (
    values: RuleEditorValues,
    {
      validateRoutingSwitchingAvailability = false,
    }: {
      validateRoutingSwitchingAvailability?: boolean;
    } = {},
  ): boolean => {
    form.clearErrors();
    setSubmitErrorMessage(null);
    const errors = getRuleEditorValidationErrors(
      values,
      existingRuleId ?? null,
    );

    if (
      validateRoutingSwitchingAvailability &&
      hasRoutingSwitchingRule &&
      values.type === 'routingSwitching'
    ) {
      errors[SHARED_RULE_ERROR_FIELD] = {
        type: 'manual',
        message: 'Only one Routing & switching rule can be added.',
      };
    }

    for (const [fieldName, error] of Object.entries(errors)) {
      if (fieldName === SHARED_RULE_ERROR_FIELD) {
        setSubmitErrorMessage(error.message ?? GENERIC_VALIDATION_ERROR);
        continue;
      }
      form.setError(fieldName as keyof RuleEditorValues, error);
    }

    return Object.keys(errors).length === 0;
  };

  const submitRule = form.handleSubmit((values) => {
    if (
      !validateRuleValues(values, {
        validateRoutingSwitchingAvailability: true,
      })
    ) {
      return;
    }

    onSubmit(createRuleFromEditorValues(values, existingRuleId));
  });

  const openTestRulePanel = form.handleSubmit((values) => {
    if (!validateRuleValues(values)) {
      return;
    }

    const nextTestValues = getTestRuleValuesFromEditor(values);
    if (!nextTestValues) {
      return;
    }
    setTestValues(nextTestValues);
    setMode('test');
  });

  const applyTestValuesToForm = (nextTestValues: TestRuleValues) => {
    form.setValue('transformation', nextTestValues.transformation, {
      shouldDirty: true,
    });

    if (nextTestValues.type === 'cloudResourceTag') {
      form.setValue('tagNames', nextTestValues.tagNames, {
        shouldDirty: true,
      });
      form.setValue(
        'applyToCloudInstances',
        nextTestValues.applyToCloudInstances,
        {
          shouldDirty: true,
        },
      );
      return;
    }

    form.setValue('regex', nextTestValues.regex, { shouldDirty: true });
    form.setValue('siteName', nextTestValues.siteName, { shouldDirty: true });

    if (nextTestValues.type === 'regexCloudResourceId') {
      form.setValue(
        'applyToCloudInstances',
        nextTestValues.applyToCloudInstances,
        {
          shouldDirty: true,
        },
      );
    }
  };

  const applyUsedExpression = () => {
    if (!testValues) {
      return;
    }

    applyTestValuesToForm(testValues);

    if (testOpenedFromTable) {
      const mergedValues = getRuleValuesWithAppliedTestValues(
        form.getValues(),
        testValues,
      );

      if (!validateRuleValues(mergedValues)) {
        setMode('edit');
        return;
      }

      onSubmit(createRuleFromEditorValues(mergedValues, existingRuleId));
      return;
    }

    setMode('edit');
  };

  const revertTestChanges = () => {
    if (testOpenedFromTable) {
      onClose();
      return;
    }

    setMode('edit');
  };

  const renderEditorContent = () => (
    <form className='flex flex-col gap-sm' onSubmit={submitRule}>
      {submitErrorMessage !== null && (
        <FormErrorMessage>{submitErrorMessage}</FormErrorMessage>
      )}
      <Card className='p-sm flex flex-col gap-sm'>
        <FormSelect.Mandatory
          control={form.control}
          dataTestId='site-separation-rule-type'
          disabled={!addMode}
          label='Used attribute'
          name='type'
          options={ruleTypeOptions}
        />
        <UsedAttributeDescription
          selectedType={selectedType}
          onClose={onClose}
          goToAuthenticationVendors={goToAuthenticationVendors}
        />
        <SelectedEditorContent control={form.control} />
      </Card>
    </form>
  );

  const renderFooter =
    mode === 'edit'
      ? ({ closeModal }: { closeModal: () => void }) => (
          <EditFooter
            isAddMode={addMode}
            closeModal={closeModal}
            onOpenTestPanel={openTestRulePanel}
            onSubmitRule={submitRule}
            testRuleAvailable={testRuleAvailable}
          />
        )
      : () => (
          <TestFooter
            onApply={applyUsedExpression}
            onRevert={revertTestChanges}
          />
        );

  const content =
    mode === 'test' && testValues ? (
      <TestRuleSidePanelContent
        initialValues={testValues}
        snapshotId={discoverySettingsContext.snapshotId}
        onValuesChange={setTestValues}
      />
    ) : (
      renderEditorContent()
    );

  return (
    <SidePanel
      title={getSidePanelTitle(mode, addMode)}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={renderFooter}
      size={mode === 'test' ? 'large' : 'default'}
    >
      {content}
    </SidePanel>
  );
};
